// API 환경별 설정

import {
  HeartWallet,
  GetTransactionsResponse,
  DailyFreeHeartsResponse,
  HeartPackage,
  PurchaseHeartPackageResponse,
  RewardPolicyDto,
  RewardAvailabilityDto,
  GrantRewardResponse,
  RewardHistoryDto,
  ConsumeHeartsResponse,
} from '../types';

export interface ApiConfig {
  baseURL: string;
  environment: string;
}

// 커스텀 API 에러 클래스 (서버 에러 데이터 보존)
export class ApiError extends Error {
  public readonly status: number;
  public readonly errorData: any;
  public readonly errorType?: string;
  public readonly errorCode?: string;

  constructor(message: string, status: number, errorData: any) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.errorData = errorData;
    // 서버 응답 구조: { error: { errorCode: "GTH_XXXX", errorType: "...", message: "..." } }
    this.errorType = errorData?.error?.errorType;
    this.errorCode = errorData?.error?.errorCode ?? this.errorType;
  }
}

// 환경별 설정
const getApiConfig = (): ApiConfig => {
  const baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8888';
  const environment = process.env.REACT_APP_ENV || 'local';

  return {
    baseURL,
    environment,
  };
};

export const API_CONFIG = getApiConfig();

// API 엔드포인트 생성 헬퍼
export const createApiUrl = (endpoint: string): string => {
  return `${API_CONFIG.baseURL}${endpoint}`;
};

// 주요 API 엔드포인트들
export const API_ENDPOINTS = {
  // 인증 관련
  AUTH: {
    LOGIN: '/api-web/v1/gth-auth/login',
    JOIN: '/api-web/v1/gth-auth/join',
    REFRESH: '/api-web/v1/gth-auth/refresh',
    LOGOUT: '/api-web/v1/gth-auth/logout',
    COMPLETE_PROFILE: '/api/v1/gth-auth/complete-profile',
    COMPLETE_KAKAO_REGISTRATION: '/api/v1/gth-auth/complete-kakao-registration',
    REGISTRATION_REQUEST: '/api/v1/gth-common/silver-connect/registration/request',
  },
  // 카카오 관련
  KAKAO: {
    CALLBACK: '/api-web/v1/gth-auth/kakao/oauth/callback',
  },
  // 사용자 관련
  USER: {
    PROFILE: '/api/v1/user/profile',
    UPDATE: '/api/v1/user/update',
  },
  // 대시보드 관련
  DASHBOARD: {
    SUMMARY: '/api/v1/gth-common/silver-connect/dashboard',
  },
  // 큐피트 카드 관련
  CARDS: {
    LIST: '/api/v1/cards',
    OPEN: (cardId: string | number) => `/api/v1/gth-common/silver-connect/cupid-cards/${cardId}/open`,
    EXTEND: (cardId: string | number) => `/api/v1/gth-common/silver-connect/cupid-cards/${cardId}/extend`,
    DETAIL: (cardId: string | number) => `/api/v1/gth-common/silver-connect/cupid-cards/${cardId}/detail`,
  },
  // 채팅 관련
  CHAT: {
    LIST: '/api/v1/chats',
    MESSAGES: '/api/v1/chats/messages',
    SEND: '/api/v1/chats/send',
  },
  // 결제 관련
  PAYMENT: {
    HEARTS: '/api/v1/payment/hearts',
  },
  // 메타 정보 관련
  META: {
    INTERESTS: '/api/v1/gth-common/silver-connect/meta/interests',
    REGIONS: '/api/v1/gth-common/silver-connect/meta/regions',
  },
  // 프로필 관련
  PROFILE: {
    GET: '/api/v1/gth-common/silver-connect/profile',
    REGISTER: '/api/v1/gth-common/silver-connect/profile/register',
    SUBMIT: '/api/v1/gth-common/silver-connect/profile/submit',
    UPDATE: '/api/v1/gth-common/silver-connect/profile/update',
    UPDATE_FREE_FIELDS: '/api/v1/gth-common/silver-connect/profile/free-fields',
    SUBMIT_REVIEW_FIELDS: '/api/v1/gth-common/silver-connect/profile/review-fields',
    UPLOAD_URL: '/api/v1/gth-common/silver-connect/profile/images/upload-url',
    RESUBMIT: '/api/v1/gth-common/silver-connect/profile/resubmit',
  },
  // 하트 시스템
  HEART: {
    BALANCE: '/api/v1/gth-common/silver-connect/hearts/balance',
    WALLET: '/api/v1/gth-common/silver-connect/hearts/wallet',
    TRANSACTIONS: '/api/v1/gth-common/silver-connect/hearts/transactions',
    DAILY_FREE: '/api/v1/gth-common/silver-connect/hearts/daily-free',
    PURCHASE: '/api/v1/gth-common/silver-connect/hearts/purchase',
    PACKAGES: '/api/v1/gth-common/silver-connect/hearts/packages',
    PACKAGES_PURCHASE: '/api/v1/gth-common/silver-connect/hearts/packages/purchase',
    REWARD_POLICIES: '/api/v1/gth-common/silver-connect/hearts/rewards/policies',
    REWARD_AVAILABILITY: '/api/v1/gth-common/silver-connect/hearts/rewards/availability',
    REWARD_GRANT: '/api/v1/gth-common/silver-connect/hearts/rewards/grant',
    REWARD_HISTORY: '/api/v1/gth-common/silver-connect/hearts/rewards/history',
    CONSUME: '/api/v1/gth-common/silver-connect/hearts/consume',
  },
  // 본인인증 관련
  IDENTITY: {
    START: '/api/v1/gth-common/identity-verification/start',
    CALLBACK: '/api/v1/gth-common/identity-verification/callback',
  },
  // 약관 관련
  TERMS: {
    ACTIVE: '/api-public/v1/gth-common/silver-connect/terms/active',
    AGREE: '/api/v1/gth-common/silver-connect/terms/agree',
    AGREE_PURCHASE: '/api/v1/gth-common/silver-connect/terms/agree/purchase',
    MARKETING: '/api/v1/gth-common/silver-connect/terms/marketing',
    CHECK: '/api/v1/gth-common/silver-connect/terms/check',
  },
  // 신고
  REPORT: {
    SUBMIT: '/api/v1/gth-common/silver-connect/reports',
  },
  // 첨부파일
  ATTACHMENT: {
    UPLOAD_URL: '/api/v1/gth-common/silver-connect/attachments/upload-url',
    REGISTER: '/api/v1/gth-common/silver-connect/attachments/register',
  },
  // FAQ (public)
  FAQ: {
    LIST: '/api-public/v1/gth-common/silver-connect/faqs',
    FEEDBACK: (id: number) => `/api/v1/gth-common/silver-connect/faqs/${id}/feedback`,
  },
  // 문의
  INQUIRY: {
    SUBMIT: '/api/v1/gth-common/silver-connect/inquiries',
    LIST: '/api/v1/gth-common/silver-connect/inquiries',
    DETAIL: (id: number) => `/api/v1/gth-common/silver-connect/inquiries/${id}`,
    REOPEN: (id: number) => `/api/v1/gth-common/silver-connect/inquiries/${id}/reopen`,
  },
  // 제재
  SANCTION: {
    MY_LIST: '/api/v1/gth-common/silver-connect/my/sanctions',
  },
  // 이의제기
  APPEAL: {
    SUBMIT: '/api/v1/gth-common/silver-connect/appeals',
    LIST: '/api/v1/gth-common/silver-connect/appeals',
    DETAIL: (id: number) => `/api/v1/gth-common/silver-connect/appeals/${id}`,
  },
  // 알림
  NOTIFICATION: {
    LIST: '/api/v1/gth-common/silver-connect/notifications',
    UNREAD_COUNT: '/api/v1/gth-common/silver-connect/notifications/unread-count',
    READ: (id: string) => `/api/v1/gth-common/silver-connect/notifications/${id}/read`,
    READ_ALL: '/api/v1/gth-common/silver-connect/notifications/read-all',
    SEEN: '/api/v1/gth-common/silver-connect/notifications/seen',
  },
  // 관리자 API
  ADMIN: {
    UPDATE_USER_STATUS: (userId: number) => `/api-admin/v1/gth-common/silver-connect/members/${userId}/status`,
    REPORTS: {
      LIST: '/api-admin/v1/gth-common/silver-connect/reports',
      DETAIL: (id: number) => `/api-admin/v1/gth-common/silver-connect/reports/${id}`,
      ASSIGN: (id: number) => `/api-admin/v1/gth-common/silver-connect/reports/${id}/assign`,
      REVIEW: (id: number) => `/api-admin/v1/gth-common/silver-connect/reports/${id}/review`,
      ACTION: (id: number) => `/api-admin/v1/gth-common/silver-connect/reports/${id}/action`,
      DISMISS: (id: number) => `/api-admin/v1/gth-common/silver-connect/reports/${id}/dismiss`,
    },
    INQUIRIES: {
      LIST: '/api-admin/v1/gth-common/silver-connect/inquiries',
      DETAIL: (id: number) => `/api-admin/v1/gth-common/silver-connect/inquiries/${id}`,
      ASSIGN: (id: number) => `/api-admin/v1/gth-common/silver-connect/inquiries/${id}/assign`,
      REPLY: (id: number) => `/api-admin/v1/gth-common/silver-connect/inquiries/${id}/reply`,
      CLOSE: (id: number) => `/api-admin/v1/gth-common/silver-connect/inquiries/${id}/close`,
    },
    APPEALS: {
      LIST: '/api-admin/v1/gth-common/silver-connect/appeals',
      DETAIL: (id: number) => `/api-admin/v1/gth-common/silver-connect/appeals/${id}`,
      ASSIGN: (id: number) => `/api-admin/v1/gth-common/silver-connect/appeals/${id}/assign`,
      REVIEW: (id: number) => `/api-admin/v1/gth-common/silver-connect/appeals/${id}/review`,
    },
    FAQS: {
      LIST: '/api-admin/v1/gth-common/silver-connect/faqs',
      DETAIL: (id: number) => `/api-admin/v1/gth-common/silver-connect/faqs/${id}`,
      CREATE: '/api-admin/v1/gth-common/silver-connect/faqs',
      UPDATE: (id: number) => `/api-admin/v1/gth-common/silver-connect/faqs/${id}`,
      DELETE: (id: number) => `/api-admin/v1/gth-common/silver-connect/faqs/${id}`,
    },
    CUPID_CARDS: {
      STATS: '/api-admin/v1/gth-common/silver-connect/cupid-cards/stats',
      BATCH_RESULTS: '/api-admin/v1/gth-common/silver-connect/cupid-cards/batch-results',
      LIST: '/api-admin/v1/gth-common/silver-connect/cupid-cards',
      DETAIL: (cardId: number) => `/api-admin/v1/gth-common/silver-connect/cupid-cards/${cardId}`,
      FORCE_EXPIRE: (cardId: number) => `/api-admin/v1/gth-common/silver-connect/cupid-cards/${cardId}/force-expire`,
      MANUAL_ISSUE: '/api-admin/v1/gth-common/silver-connect/cupid-cards/manual-issue',
    },
    HEARTS: {
      GRANT: (userId: number) => `/api-admin/v1/gth-common/silver-connect/users/${userId}/hearts/grant`,
    },
    PROFILE_REVIEWS: {
      LIST: '/api/v1/gth-common/admin/silver-connect/profile-reviews',
      DETAIL: (id: number) => `/api/v1/gth-common/admin/silver-connect/profile-reviews/${id}`,
      APPROVE: (id: number) => `/api/v1/gth-common/admin/silver-connect/profile-reviews/${id}/approve`,
      REJECT: (id: number) => `/api/v1/gth-common/admin/silver-connect/profile-reviews/${id}/reject`,
    },
  },
};

// API 클라이언트 헬퍼
export class ApiClient {
  private baseURL: string;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<any> | null = null;

  constructor() {
    this.baseURL = API_CONFIG.baseURL;
  }

  // Refresh 토큰을 사용해서 액세스 토큰 갱신 (쿠키 사용)
  private async refreshAccessToken(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseURL}${API_ENDPOINTS.AUTH.REFRESH}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // 쿠키를 포함하여 요청
      });

      if (!response.ok) {
        // Refresh 토큰도 만료된 경우
        this.clearAuthData(); // 모든 인증 정보 정리
        window.location.href = '/login'; // 로그인 페이지로 이동
        return false;
      }

      const data = await response.json();
      if (data.resultType === 'SUCCESS' && data.result.tokenDto) {
        localStorage.setItem('accessToken', data.result.tokenDto.accessToken);
        // refreshToken은 더 이상 localStorage에 저장하지 않음 (HttpOnly 쿠키)
        return true;
      }

      return false;
    } catch (error) {
      console.error('토큰 갱신 실패:', error);
      this.clearAuthData(); // 모든 인증 정보 정리
      window.location.href = '/login';
      return false;
    }
  }

  // 기본 fetch 래퍼
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry: boolean = false
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const token = localStorage.getItem('accessToken');
    const userId = localStorage.getItem('userId');

    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...(userId && { 'KEY_USER_ID': userId }),
        ...options.headers,
      },
      credentials: 'include', // 쿠키를 포함하여 요청
      ...options,
    };

    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // 401 에러이고 EXPIRED_TOKEN 에러이며 아직 재시도하지 않은 경우
      // GTH_4031(errorCode) 또는 EXPIRED_TOKEN(errorType) 둘 다 체크 — 다른 401(GTH_4030 권한 없음)은 갱신 안 함
      const isExpiredToken =
        errorData.error?.errorCode === 'GTH_4031' ||
        errorData.error?.errorType === 'EXPIRED_TOKEN';
      if (response.status === 401 &&
          isExpiredToken &&
          !isRetry &&
          endpoint !== API_ENDPOINTS.AUTH.REFRESH && // refresh 요청 자체에서는 재시도 안함
          endpoint !== API_ENDPOINTS.AUTH.LOGIN && // 로그인 요청에서는 재시도 안함
          endpoint !== API_ENDPOINTS.AUTH.JOIN) { // 회원가입 요청에서는 재시도 안함
        
        // 이미 다른 요청에서 토큰 갱신 중이면 기다림
        if (this.isRefreshing) {
          try {
            await this.refreshPromise;
            // 토큰 갱신 완료 후 원래 요청 재시도
            return this.request<T>(endpoint, options, true);
          } catch (refreshError) {
            throw new ApiError(
              errorData.error?.message || errorData.message || `HTTP Error: ${response.status}`,
              response.status,
              errorData
            );
          }
        }

        // 토큰 갱신 시도
        this.isRefreshing = true;
        this.refreshPromise = this.refreshAccessToken();
        
        try {
          const refreshSuccess = await this.refreshPromise;
          if (refreshSuccess) {
            // 토큰 갱신 성공시 원래 요청 재시도
            return this.request<T>(endpoint, options, true);
          } else {
            throw new ApiError(
              errorData.error?.message || errorData.message || `HTTP Error: ${response.status}`,
              response.status,
              errorData
            );
          }
        } finally {
          this.isRefreshing = false;
          this.refreshPromise = null;
        }
      }

      throw new ApiError(
        errorData.error?.message || errorData.message || `HTTP Error: ${response.status}`,
        response.status,
        errorData
      );
    }

    return response.json();
  }

  // GET 요청
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  // POST 요청
  async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PUT 요청
  async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // PATCH 요청
  async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // DELETE 요청
  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  // Basic Auth 로그인 (이메일 로그인용)
  async basicAuthPost<T>(endpoint: string, email: string, password: string): Promise<T> {
    const credentials = `${email}:${password}`;
    const encodedCredentials = btoa(credentials);
    
    return this.request<T>(endpoint, {
      method: 'POST',
      headers: {
        'Authorization': `Basic ${encodedCredentials}`,
      },
    });
  }

  // 로그아웃 (refreshToken 쿠키 제거)
  async logout(): Promise<void> {
    try {
      await this.post(API_ENDPOINTS.AUTH.LOGOUT);
      // 로컬 스토리지에서 액세스 토큰 제거
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
    } catch (error) {
      console.error('로그아웃 중 오류 발생:', error);
      // 에러가 발생해도 로컬 토큰은 제거
      localStorage.removeItem('accessToken');
      localStorage.removeItem('user');
    }
  }

  // 모든 인증 정보 정리 (로그인/회원가입 페이지 진입 시 사용)
  clearAuthData(): void {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    sessionStorage.removeItem('processedKakaoCode');
    sessionStorage.removeItem('registrationUserData');
    sessionStorage.removeItem('kakaoUserData');
  }

  // ========== 하트 시스템 API ==========

  async getHeartBalance(): Promise<{ balance: number }> {
    const res = await this.get<{ result: { balance: number } }>(API_ENDPOINTS.HEART.BALANCE);
    return res.result;
  }

  async getHeartWallet(): Promise<HeartWallet> {
    const res = await this.get<{ result: HeartWallet }>(API_ENDPOINTS.HEART.WALLET);
    return res.result;
  }

  async getHeartTransactions(params: {
    page?: number;
    size?: number;
    type?: string;
  } = {}): Promise<GetTransactionsResponse> {
    const { page = 0, size = 20, type } = params;
    const qs = new URLSearchParams({ page: String(page), size: String(size) });
    if (type) qs.append('type', type);
    const res = await this.get<{ result: GetTransactionsResponse }>(
      `${API_ENDPOINTS.HEART.TRANSACTIONS}?${qs.toString()}`,
    );
    return res.result;
  }

  async claimDailyFreeHearts(): Promise<DailyFreeHeartsResponse> {
    const res = await this.post<{ result: DailyFreeHeartsResponse }>(API_ENDPOINTS.HEART.DAILY_FREE);
    return res.result;
  }

  async getHeartPackages(): Promise<HeartPackage[]> {
    const res = await this.get<{ result: HeartPackage[] }>(API_ENDPOINTS.HEART.PACKAGES);
    return res.result;
  }

  async purchaseHeartPackage(request: {
    packageId: number;
    paymentMethod?: string;
    paymentReferenceId?: string;
  }): Promise<PurchaseHeartPackageResponse> {
    const res = await this.post<{ result: PurchaseHeartPackageResponse }>(
      API_ENDPOINTS.HEART.PACKAGES_PURCHASE,
      request,
    );
    return res.result;
  }

  async getRewardPolicies(): Promise<RewardPolicyDto[]> {
    const res = await this.get<{ result: RewardPolicyDto[] }>(API_ENDPOINTS.HEART.REWARD_POLICIES);
    return res.result;
  }

  async checkRewardAvailability(rewardType: string): Promise<RewardAvailabilityDto> {
    const res = await this.get<{ result: RewardAvailabilityDto }>(
      `${API_ENDPOINTS.HEART.REWARD_AVAILABILITY}?rewardType=${rewardType}`,
    );
    return res.result;
  }

  async grantReward(request: {
    rewardType: string;
    description?: string;
  }): Promise<GrantRewardResponse> {
    const res = await this.post<{ result: GrantRewardResponse }>(
      API_ENDPOINTS.HEART.REWARD_GRANT,
      request,
    );
    return res.result;
  }

  async getRewardHistory(page: number = 0, size: number = 20): Promise<RewardHistoryDto[]> {
    const res = await this.get<{ result: RewardHistoryDto[] }>(
      `${API_ENDPOINTS.HEART.REWARD_HISTORY}?page=${page}&size=${size}`,
    );
    return res.result;
  }

  async consumeHeart(request: {
    transactionType: string;
    referenceId?: number;
  }): Promise<ConsumeHeartsResponse> {
    const res = await this.post<{ result: ConsumeHeartsResponse }>(
      API_ENDPOINTS.HEART.CONSUME,
      request,
    );
    return res.result;
  }

  // ========== 본인인증 API ==========

  async startIdentityVerification(): Promise<{ txId: string }> {
    const res = await this.post<{ result: { txId: string } }>(
      API_ENDPOINTS.IDENTITY.START,
    );
    return res.result;
  }

  async completeIdentityVerification(txId: string): Promise<{
    wasUpdated: boolean;
  }> {
    const res = await this.post<{
      result: {
        wasUpdated: boolean;
      };
    }>(API_ENDPOINTS.IDENTITY.CALLBACK, { txId });
    return res.result;
  }

  async submitProfile(): Promise<void> {
    await this.post(API_ENDPOINTS.PROFILE.SUBMIT);
  }

  async getProfile(): Promise<{
    identityVerified: boolean;
  } | null> {
    try {
      const res = await this.get<{ result: { identityVerified: boolean } }>(API_ENDPOINTS.PROFILE.GET);
      return res.result;
    } catch (e: any) {
      // GTH_4042 = NOT_FOUND_PROFILE → profile doesn't exist yet
      if (e?.errorCode === 'GTH_4042') return null;
      throw e;
    }
  }
}

// 싱글톤 인스턴스
export const apiClient = new ApiClient();

